import { Platform } from 'react-native';

/**
 * Professional Typography System
 *
 * Designed for readability and professional coaching platform hierarchy
 * Optimized for both mobile and web platforms
 */

export const typography = {
  // Font Families
  fonts: {
    primary: Platform.select({
      ios: 'SF Pro Display',
      android: 'Roboto',
      web: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", "Roboto", system-ui, sans-serif',
      default: 'System',
    }),
    mono: Platform.select({
      ios: 'SF Mono',
      android: 'monospace',
      web: '"SF Mono", "Monaco", "Consolas", "Courier New", monospace',
      default: 'monospace',
    }),
  },

  // Font Weights
  weights: {
    regular: '400' as const,
    medium: '500' as const,
    semibold: '600' as const,
    bold: '700' as const,
  },

  // Typography Scale
  display: {
    fontSize: 32,
    lineHeight: 40,
    fontWeight: '700' as const,
    letterSpacing: -0.5,
  },

  heading1: {
    fontSize: 24,
    lineHeight: 32,
    fontWeight: '600' as const,
    letterSpacing: -0.25,
  },

  heading2: {
    fontSize: 20,
    lineHeight: 28,
    fontWeight: '600' as const,
    letterSpacing: -0.15,
  },

  heading3: {
    fontSize: 18,
    lineHeight: 24,
    fontWeight: '500' as const,
    letterSpacing: -0.1,
  },

  bodyLarge: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '400' as const,
    letterSpacing: 0,
  },

  body: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400' as const,
    letterSpacing: 0,
  },

  caption: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '400' as const,
    letterSpacing: 0.25,
  },

  button: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '500' as const,
    letterSpacing: 0.1,
  },

  label: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '500' as const,
    letterSpacing: 0.5,
  },
} as const;

// Helper function to create text styles
export const createTextStyle = (variant: keyof typeof typography, fontFamily?: string) => {
  const style = typography[variant];
  return {
    ...style,
    fontFamily: fontFamily || typography.fonts.primary,
  };
};

export type Typography = typeof typography;
export type TypographyVariant = keyof typeof typography;

export default typography;